import React, { useCallback, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import moment from "moment-timezone";
import Tooltip from "@mui/material/Tooltip";

import ai from "../../../helpers/axios";
import shortenName from "../../../helpers/shorten-name";
import currencyFormatter from "../../../helpers/currencyFormatter";

const formatDate = (date) => (date ? moment(date).format("M/D/YYYY") : "N/A");

const openL2 = (path) => window.open(path, "_blank", "noopener,noreferrer");

const buildRows = (trip, messages) => {
  const rows = [];

  (trip?.tripNotes || []).forEach((note) => {
    rows.push({
      key: `note-${note._id}`,
      date: note.createdAt,
      type: `Trip Note (${note.category || "N/A"})`,
      createdBy: shortenName(note.createdBy?.name) || "N/A",
      content: <span className="tv-truncate tv-cell">{note.note}</span>,
      tooltip: note.note,
    });
  });

  (messages || []).forEach((message) => {
    rows.push({
      key: `msg-${message._id}`,
      date: message.timestamp,
      type: "Internal Message",
      createdBy: shortenName(message.sender?.name) || "N/A",
      content: <span className="tv-truncate tv-cell">{message.contents}</span>,
      tooltip: message.contents,
    });
  });

  const pt = trip?.passthroughData;
  if (pt) {
    const commission =
      pt.expectedCommission || pt.expectedCommission === 0
        ? currencyFormatter.format(pt.expectedCommission)
        : "N/A";
    rows.push({
      key: "passthrough",
      date: pt.convertedAt,
      type: "Passthrough",
      createdBy: shortenName(pt.convertedBy?.name) || "N/A",
      content: (
        <div className="tv-passthrough-content">
          <div>{`${pt.operator?.label || pt.operator?.name || "N/A"} - ${commission}`}</div>
          <div className="tv-truncate tv-cell">{pt.notes}</div>
        </div>
      ),
      tooltip: pt.notes,
    });
  }

  return rows.sort(
    (a, b) => new Date(a.date || 0) - new Date(b.date || 0),
  );
};

const NotesMessagesBlock = ({ tripId, trip, readOnly }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [messages, setMessages] = useState([]);

  const loadMessages = useCallback(async () => {
    if (!tripId) return;
    try {
      const token = await getAccessTokenSilently();
      const { data } = await ai
        .auth(token)
        .get(`/api/messages/list/${tripId}?recordType=trip`);
      setMessages(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error loading internal messages:", error);
      setMessages([]);
    }
  }, [tripId, getAccessTokenSilently]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const isPassthrough = !!trip?.passthroughData;
  const rows = buildRows(trip, messages);

  return (
    <div className="tv-block tv-notes-messages">
      <div className="tv-block-header">
        <h5 className="tv-block-title">Notes &amp; Messages</h5>
        <div className="tv-block-actions">
          {!readOnly && (
            <button
              type="button"
              className="base-button icon-button icon-button-small evo-blue"
              onClick={() => openL2(`/trips/${tripId}?tab=tripNotes`)}
              title="Add/Edit Trip Note"
            >
              <span className="material-icons">add_circle</span>
            </button>
          )}
          <button
            type="button"
            className="base-button icon-button icon-button-small evo-blue"
            onClick={() =>
              openL2(`/trips/${tripId}?tab=messages&messagesTab=internal`)
            }
            title="Send Message"
          >
            <span className="material-icons">send</span>
          </button>
          {isPassthrough && !readOnly && (
            <button
              type="button"
              className="base-button icon-button icon-button-small evo-blue"
              onClick={() => openL2(`/trips/${tripId}`)}
              title="Edit Passthrough"
            >
              <span className="material-icons">edit</span>
            </button>
          )}
        </div>
      </div>

      <div className="tv-block-body">
        {rows.length === 0 ? (
          <div className="tv-empty">No notes or messages are available.</div>
        ) : (
          <table className="tv-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Created by</th>
                <th>Content</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key}>
                  <td>{formatDate(row.date)}</td>
                  <td>{row.type}</td>
                  <td>{row.createdBy}</td>
                  <td>
                    <Tooltip title={row.tooltip || ""} arrow>
                      {row.content}
                    </Tooltip>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default NotesMessagesBlock;
